import { useTranslation } from '../context/LanguageContext';
import { useUnifiedWallet } from './auth/useWallet';
import { LoginScreen } from './components/LoginScreen';
import { Dashboard } from './components/Dashboard';
import { Header } from './components/Header';
import { ExportKeyBanner } from './components/ExportKeyBanner';
import './sunset.css';

/**
 * Inner sunset shell. Mounted by SunsetRoute in App.tsx, which provides:
 *  - ThemeProvider + LanguageProvider (shared with the rest of the app)
 *  - PrivyProvider (sunset-specific config: createOnLogin: 'off')
 *  - ConnectionProvider + WalletProvider + WalletModalProvider for the
 *    native @solana/wallet-adapter-react path
 *
 * Wraps everything in .mm-sunset-root so sunset.css selectors don't bleed
 * into normal mining-mode pages.
 */
export function SunsetApp() {
  const { t } = useTranslation();
  const wallet = useUnifiedWallet();

  return (
    <div className="mm-sunset-root">
      <div className="sunset-page">
        <Header wallet={wallet} />
        <main className="sunset-main">
          {(() => {
            const [before, after] = t('sunset.notice.line2').split('{link}');
            return (
              <div className="win98-window win98-mb-md">
                <div className="win98-title-bar">
                  <div className="win98-title">
                    <i className="fa-solid fa-circle-info" /> {t('sunset.notice.title')}
                  </div>
                </div>
                <div className="win98-content">
                  <p className="win98-text-normal win98-mb-xs">
                    {t('sunset.notice.line1')}
                  </p>
                  <p className="win98-text-normal">
                    {before}
                    <a
                      href="https://orestack.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="win98-link mm-sunset-notice-link"
                    >
                      orestack.app
                    </a>
                    {after}
                  </p>
                </div>
              </div>
            );
          })()}
          {wallet.isConnected && wallet.source === 'privy' && (
            <ExportKeyBanner wallet={wallet} />
          )}
          {!wallet.isReady ? (
            <div className="win98-window">
              <div className="win98-title-bar">
                <div className="win98-title">
                  <i className="fa-solid fa-hourglass-half" /> {t('sunset.loadingTitle')}
                </div>
              </div>
              <div className="win98-content">
                <p className="win98-text-normal">{t('sunset.loadingMessage')}</p>
              </div>
            </div>
          ) : wallet.isConnected ? (
            <Dashboard wallet={wallet} />
          ) : (
            <LoginScreen wallet={wallet} />
          )}
        </main>
        <footer className="sunset-footer">{t('sunset.footer')} 2026</footer>
      </div>
    </div>
  );
}
